import { readonly as vueReadonly } from "vue";
import { logger } from "~/utils/logger";
import { io, Socket } from "socket.io-client";
import { useBackendUrl } from "~/utils/backend-url";
import type {
  InventoryUpdatePayload,
  AvailableDatesUpdatePayload,
  BookingStatusUpdatePayload,
  AdminNotificationPayload,
  PaymentStatusUpdatePayload,
  DocumentUploadedPayload,
  DocumentStatusUpdatePayload,
  OrderDocumentUploadedPayload,
  OrderNoteAddedPayload,
  OrderNoteDeletedPayload,
} from "~/types/socket-events";
import { SOCKET_EVENTS } from "~/types/socket-events";

/**
 * Socket.IO Composable for Real-Time Communication
 * Provides a reactive interface to WebSocket connections
 */
export const useWebSocket = () => {
  // Socket instance (singleton per session)
  const socket = useState<Socket | null>("socket", () => null);
  const isConnected = useState("socket:connected", () => false);
  const isAuthenticated = useState("socket:authenticated", () => false);
  const connectionError = useState<string | null>("socket:error", () => null);

  const config = useRuntimeConfig();
  const authStore = useAuthStore?.();

  // Track reconnection attempts for user feedback
  const reconnectionAttempts = useState("socket:reconnectionAttempts", () => 0);

  // Store active subscriptions so we can re-subscribe on reconnection
  const activeSubscriptions = useState<Set<string>>(
    "socket:subscriptions",
    () => new Set(),
  );

  /**
   * Initialize Socket.IO connection
   */
  const connect = () => {
    if (socket.value?.connected) {
      logger.log("✅ Socket already connected");
      return;
    }

    // Get backend URL from centralized utility
    const { baseUrl } = useBackendUrl();

    logger.log("🔌 Connecting to WebSocket server:", baseUrl);

    try {
      socket.value = io(baseUrl, {
        transports: ["websocket", "polling"],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: 5,
        autoConnect: true,
        withCredentials: true, // CRITICAL: Send cookies with handshake for httpOnly auth
      });

      // Connection event handlers
      socket.value.on("connect", () => {
        logger.log("✅ Socket connected:", socket.value?.id);
        isConnected.value = true;
        connectionError.value = null;
        reconnectionAttempts.value = 0;

        // Auto-authenticate if user is logged in
        if (authStore?.user && authStore?.isAuthenticated) {
          const isAdmin =
            authStore.user.role === "ADMIN" ||
            authStore.user.role === "BOOKING_MANAGER";
          authenticate(authStore.user.id, isAdmin);
        }

        // Re-subscribe to all active subscriptions on reconnect
        if (activeSubscriptions.value.size > 0) {
          logger.log(
            `🔄 Re-subscribing to ${activeSubscriptions.value.size} package(s)...`,
          );
          activeSubscriptions.value.forEach((subscriptionKey) => {
            const [packageId, departureDate, inventoryId] =
              subscriptionKey.split("|");
            socket.value?.emit(SOCKET_EVENTS.SUBSCRIBE_TO_PACKAGE, {
              packageId,
              departureDate: departureDate || undefined,
              inventoryId: inventoryId || undefined,
            });
          });
        }
      });

      socket.value.on("disconnect", (reason) => {
        logger.log("🔌 Socket disconnected:", reason);
        isConnected.value = false;
        isAuthenticated.value = false;

        // Only log user-friendly messages for expected disconnections
        if (reason === "io server disconnect") {
          // Server intentionally closed - this is normal during restart
          logger.log("Server closed connection (possibly restarting)");
        } else if (reason === "transport close") {
          // Network issue - will auto-reconnect
          logger.log("Connection lost - reconnecting...");
        }
      });

      socket.value.on("connect_error", (error) => {
        // Don't log as error during normal reconnect attempts
        // Only log info to avoid cluttering console during backend restarts
        logger.log("Socket connection attempt:", error.message);
        isConnected.value = false;
      });

      // Global error handler for socket errors
      socket.value.on("error", (error) => {
        // Log as info instead of error to reduce noise during normal operations
        logger.log("Socket event:", error);
      });

      // Reconnection event handlers for user feedback
      socket.value.io.on("reconnect_attempt", (attemptNumber) => {
        logger.log(`🔄 Reconnection attempt ${attemptNumber}...`);
        reconnectionAttempts.value = attemptNumber;
      });

      socket.value.io.on("reconnect", (attemptNumber) => {
        logger.log(`✅ Reconnected after ${attemptNumber} attempts`);
        connectionError.value = null;
        reconnectionAttempts.value = 0;
      });

      socket.value.io.on("reconnect_error", (error) => {
        // Only log as info during reconnect attempts
        logger.log("Reconnection in progress:", error.message);
      });

      socket.value.io.on("reconnect_failed", () => {
        // This is a real error - all attempts exhausted
        logger.error("❌ All reconnection attempts failed");
        connectionError.value =
          "Unable to reconnect to server. Please refresh the page.";
      });

      socket.value.on(SOCKET_EVENTS.CONNECTED, (data: any) => {
        logger.log("📡 Connection confirmed:", data);
      });

      socket.value.on(
        SOCKET_EVENTS.AUTHENTICATION_SUCCESS,
        (data: { userId: string; isAdmin: boolean; timestamp: string }) => {
          logger.log("🔐 Authentication successful:", data);
          isAuthenticated.value = true;
        },
      );

      socket.value.on(
        SOCKET_EVENTS.AUTHENTICATION_ERROR,
        (data: {
          message: string;
          timestamp: string;
          requireReconnect?: boolean;
        }) => {
          // Don't log as error if it's just "anonymous connection allowed"
          if (data.message.includes("anonymous")) {
            logger.log("ℹ️ Socket auth:", data.message);
          } else {
            logger.error("🔐 Authentication failed:", data.message);
          }
          isAuthenticated.value = false;

          // Handle session change requiring reconnect
          if (data.requireReconnect) {
            logger.log("🔄 Session changed - forcing reconnect");
            disconnect();
            setTimeout(() => connect(), 1000);
          }
        },
      );
    } catch (err) {
      logger.error("❌ Failed to initialize socket:", err);
      connectionError.value = "Failed to initialize socket connection";
    }
  };

  /**
   * Authenticate the socket connection
   * Note: With httpOnly cookies, auto-authentication happens during connection handshake.
   * This method is a fallback for edge cases or to trigger a re-auth check.
   */
  const authenticate = (userId: string, isAdmin: boolean = false) => {
    // Check both our state and socket's connected property
    // Use isConnected.value as primary since socket.connected may have timing issues
    if (!socket.value || (!isConnected.value && !socket.value.connected)) {
      logger.warn("⚠️ Cannot authenticate: socket not connected");
      return;
    }

    // With httpOnly cookies, the server should have auto-authenticated us
    // from the cookie in the handshake. Just emit to trigger confirmation.
    logger.log("🔐 Requesting auth confirmation...", { userId, isAdmin });

    socket.value.emit(SOCKET_EVENTS.AUTHENTICATE, {
      userId,
      isAdmin,
      // Token is not accessible with httpOnly cookies - server reads from handshake
    });
  };

  /**
   * Disconnect from socket server
   */
  const disconnect = () => {
    if (socket.value) {
      logger.log("🔌 Disconnecting socket...");
      socket.value.disconnect();
      socket.value = null;
      isConnected.value = false;
      isAuthenticated.value = false;
    }
  };

  /**
   * Ensure socket is connected before performing operations
   * Returns a promise that resolves when connected or rejects on timeout
   */
  const ensureConnected = (timeoutMs = 5000): Promise<void> => {
    return new Promise((resolve, reject) => {
      // Already connected
      if (socket.value?.connected) {
        resolve();
        return;
      }

      // Start connection if not exists
      if (!socket.value) {
        connect();
      }

      // Wait for connection
      const timeout = setTimeout(() => {
        reject(new Error("Socket connection timeout"));
      }, timeoutMs);

      const checkConnection = () => {
        if (socket.value?.connected) {
          clearTimeout(timeout);
          resolve();
        } else {
          // Check again in 100ms
          setTimeout(checkConnection, 100);
        }
      };

      checkConnection();
    });
  };

  /**
   * Subscribe to package inventory updates
   */
  const subscribeToPackage = async (
    packageId: string,
    departureDate?: string,
    inventoryId?: string,
  ): Promise<void> => {
    try {
      // Ensure socket is connected before subscribing
      await ensureConnected();

      logger.log(
        `📦 Subscribing to: ${packageId}${
          departureDate ? ` on ${departureDate}` : ""
        }${inventoryId ? ` (${inventoryId})` : ""}`,
      );

      // Track subscription for reconnection
      const subscriptionKey = `${packageId}${departureDate ? `|${departureDate}` : ""}${inventoryId ? `|${inventoryId}` : ""}`;
      activeSubscriptions.value.add(subscriptionKey);

      socket.value!.emit(SOCKET_EVENTS.SUBSCRIBE_TO_PACKAGE, {
        packageId,
        departureDate,
        inventoryId,
      });
    } catch (error) {
      logger.error("❌ Failed to subscribe to package:", error);
      throw error;
    }
  };
  /**
   * Subscribe to booking updates
   */
  const subscribeToBooking = (bookingId: string): void => {
    if (!socket.value?.connected) {
      logger.warn("⚠️ Cannot subscribe: socket not connected");
      return;
    }

    logger.log("📋 Subscribing to booking:", bookingId);

    socket.value.emit(SOCKET_EVENTS.SUBSCRIBE_TO_BOOKING, {
      bookingId,
    });
  };

  /**
   * Subscribe to order updates
   */
  const subscribeToOrder = (orderId: string): void => {
    if (!socket.value?.connected) {
      logger.warn("⚠️ Cannot subscribe: socket not connected");
      return;
    }

    logger.log("📝 Subscribing to order:", orderId);

    socket.value.emit("subscribe:order", {
      orderId,
    });
  };

  /**
   * Subscribe to departure updates (for room assignments page)
   */
  const subscribeToDeparture = (departureId: string): void => {
    if (!socket.value?.connected) {
      logger.warn("⚠️ Cannot subscribe: socket not connected");
      return;
    }

    logger.log("🚌 Subscribing to departure:", departureId);

    socket.value.emit("subscribe:departure", {
      departureId,
    });
  };

  /**
   * Subscribe to traveler document updates
   */
  const subscribeToTraveler = (travelerId: string): void => {
    if (!socket.value?.connected) {
      logger.warn("⚠️ Cannot subscribe: socket not connected");
      return;
    }

    logger.log("👤 Subscribing to traveler documents:", travelerId);

    socket.value.emit(SOCKET_EVENTS.SUBSCRIBE_TO_TRAVELER, {
      travelerId,
    });
  };

  /**
   * Unsubscribe from a room
   */
  const unsubscribe = (room: string): void => {
    if (!socket.value?.connected) {
      return;
    }

    logger.log("🚫 Unsubscribing from room:", room);

    socket.value.emit(SOCKET_EVENTS.UNSUBSCRIBE, {
      room,
    });
  };

  /**
   * Listen to inventory updates
   */
  const onInventoryUpdate = (
    callback: (data: InventoryUpdatePayload) => void,
  ) => {
    if (!socket.value) {
      logger.warn("⚠️ Socket not initialized");
      return;
    }

    logger.log(
      `📝 Registering listener for: ${SOCKET_EVENTS.INVENTORY_UPDATE}`,
    );
    socket.value.on(SOCKET_EVENTS.INVENTORY_UPDATE, callback);
  };

  /**
   * Listen to available dates updates
   */
  const onAvailableDatesUpdate = (
    callback: (data: AvailableDatesUpdatePayload) => void,
  ) => {
    if (!socket.value) {
      logger.warn("⚠️ Socket not initialized");
      return;
    }

    socket.value.on(SOCKET_EVENTS.AVAILABLE_DATES_UPDATE, callback);
  };

  /**
   * Listen to booking status updates
   */
  const onBookingStatusUpdate = (
    callback: (data: BookingStatusUpdatePayload) => void,
  ) => {
    if (!socket.value) {
      logger.warn("⚠️ Socket not initialized");
      return;
    }

    socket.value.on(SOCKET_EVENTS.BOOKING_STATUS_UPDATE, callback);
  };

  /**
   * Listen to new booking events
   */
  const onNewBooking = (callback: (data: any) => void) => {
    if (!socket.value) {
      logger.warn("⚠️ Socket not initialized");
      return;
    }

    logger.log(`✨ Registering listener for: ${SOCKET_EVENTS.NEW_BOOKING}`);
    socket.value.on(SOCKET_EVENTS.NEW_BOOKING, (data) => {
      logger.log("✨ New booking event received:", data);
      callback(data);
    });
  };

  /**
   * Listen to admin notifications
   */
  const onAdminNotification = (
    callback: (data: AdminNotificationPayload) => void,
  ) => {
    if (!socket.value) {
      logger.warn("⚠️ Socket not initialized");
      return;
    }

    socket.value.on(SOCKET_EVENTS.ADMIN_NOTIFICATION, callback);
  };

  /**
   * Listen to user notifications (notification:new)
   */
  const onNotification = (callback: (data: any) => void) => {
    if (!socket.value) {
      logger.warn("⚠️ Socket not initialized");
      return;
    }

    logger.log(
      `🔔 Registering listener for: ${SOCKET_EVENTS.NOTIFICATION_NEW}`,
    );
    socket.value.on(SOCKET_EVENTS.NOTIFICATION_NEW, callback);
  };

  /**
   * Listen to payment status updates
   */
  const onPaymentStatusUpdate = (
    callback: (data: PaymentStatusUpdatePayload) => void,
  ) => {
    if (!socket.value) {
      logger.warn("⚠️ Socket not initialized");
      return;
    }

    socket.value.on(SOCKET_EVENTS.PAYMENT_STATUS_UPDATE, callback);
  };

  /**
   * Listen to low stock alerts (for admins)
   */
  const onLowStockAlert = (callback: (data: any) => void) => {
    if (!socket.value) {
      logger.warn("⚠️ Socket not initialized");
      return;
    }

    socket.value.on(SOCKET_EVENTS.INVENTORY_LOW_STOCK, callback);
  };

  /**
   * Listen to document uploaded events
   */
  const onDocumentUploaded = (
    callback: (data: DocumentUploadedPayload) => void,
  ) => {
    if (!socket.value) {
      logger.warn("⚠️ Socket not initialized");
      return;
    }

    logger.log(
      `📄 Registering listener for: ${SOCKET_EVENTS.DOCUMENT_UPLOADED}`,
    );
    socket.value.on(SOCKET_EVENTS.DOCUMENT_UPLOADED, (data) => {
      logger.log("📄 Document uploaded event received:", data);
      callback(data);
    });
  };

  /**
   * Listen to document status update events
   */
  const onDocumentStatusUpdate = (
    callback: (data: DocumentStatusUpdatePayload) => void,
  ) => {
    if (!socket.value) {
      logger.warn("⚠️ Socket not initialized");
      return;
    }

    logger.log(
      `📄 Registering listener for: ${SOCKET_EVENTS.DOCUMENT_STATUS_UPDATE}`,
    );
    socket.value.on(SOCKET_EVENTS.DOCUMENT_STATUS_UPDATE, (data) => {
      logger.log("📄 Document status update event received:", data);
      callback(data);
    });
  };

  /**
   * Listen to document verified events
   */
  const onDocumentVerified = (
    callback: (data: DocumentStatusUpdatePayload) => void,
  ) => {
    if (!socket.value) {
      logger.warn("⚠️ Socket not initialized");
      return;
    }

    logger.log(
      `✅ Registering listener for: ${SOCKET_EVENTS.DOCUMENT_VERIFIED}`,
    );
    socket.value.on(SOCKET_EVENTS.DOCUMENT_VERIFIED, callback);
  };

  /**
   * Listen to document rejected events
   */
  const onDocumentRejected = (
    callback: (data: DocumentStatusUpdatePayload) => void,
  ) => {
    if (!socket.value) {
      logger.warn("⚠️ Socket not initialized");
      return;
    }

    logger.log(
      `❌ Registering listener for: ${SOCKET_EVENTS.DOCUMENT_REJECTED}`,
    );
    socket.value.on(SOCKET_EVENTS.DOCUMENT_REJECTED, callback);
  };

  /**
   * Listen to order document uploaded events
   */
  const onOrderDocumentUploaded = (
    callback: (data: OrderDocumentUploadedPayload) => void,
  ) => {
    if (!socket.value) {
      logger.warn("⚠️ Socket not initialized");
      return;
    }

    logger.log(
      `📎 Registering listener for: ${SOCKET_EVENTS.ORDER_DOCUMENT_UPLOADED}`,
    );
    socket.value.on(SOCKET_EVENTS.ORDER_DOCUMENT_UPLOADED, callback);
  };

  /**
   * Listen to order note added events
   */
  const onOrderNoteAdded = (
    callback: (data: OrderNoteAddedPayload) => void,
  ) => {
    if (!socket.value) {
      logger.warn("⚠️ Socket not initialized");
      return;
    }

    logger.log(
      `📝 Registering listener for: ${SOCKET_EVENTS.ORDER_NOTE_ADDED}`,
    );
    socket.value.on(SOCKET_EVENTS.ORDER_NOTE_ADDED, callback);
  };

  /**
   * Listen to order note deleted events
   */
  const onOrderNoteDeleted = (
    callback: (data: OrderNoteDeletedPayload) => void,
  ) => {
    if (!socket.value) {
      logger.warn("⚠️ Socket not initialized");
      return;
    }

    logger.log(
      `🗑️ Registering listener for: ${SOCKET_EVENTS.ORDER_NOTE_DELETED}`,
    );
    socket.value.on(SOCKET_EVENTS.ORDER_NOTE_DELETED, callback);
  };

  /**
   * Remove event listener
   */
  const off = (event: string, callback?: (...args: any[]) => void) => {
    if (!socket.value) {
      return;
    }

    if (callback) {
      socket.value.off(event, callback);
    } else {
      socket.value.off(event);
    }
  };

  /**
   * Remove all listeners for an event
   */
  const removeAllListeners = (event?: string) => {
    if (!socket.value) {
      return;
    }

    socket.value.removeAllListeners(event);
  };

  /**
   * Enable debug mode - logs all socket events
   */
  const enableDebug = () => {
    if (!socket.value) {
      logger.warn("⚠️ Socket not initialized");
      return;
    }

    socket.value.onAny((eventName: string, ...args: any[]) => {
      logger.log(`🔍 [Socket Event] ${eventName}:`, args);
    });

    logger.log("🐛 Socket debug mode enabled");
  };

  return {
    // State
    socket: vueReadonly(socket),
    isConnected: vueReadonly(isConnected),
    isAuthenticated: vueReadonly(isAuthenticated),
    connectionError: vueReadonly(connectionError),

    // Methods
    connect,
    disconnect,
    authenticate,
    ensureConnected,

    // Subscriptions
    subscribeToPackage,
    subscribeToBooking,
    subscribeToOrder,
    subscribeToDeparture,
    subscribeToTraveler,
    unsubscribe,

    // Event Listeners
    onInventoryUpdate,
    onAvailableDatesUpdate,
    onBookingStatusUpdate,
    onNewBooking,
    onAdminNotification,
    onNotification,
    onPaymentStatusUpdate,
    onLowStockAlert,
    onDocumentUploaded,
    onDocumentStatusUpdate,
    onDocumentVerified,
    onDocumentRejected,
    onOrderDocumentUploaded,
    onOrderNoteAdded,
    onOrderNoteDeleted,
    off,
    removeAllListeners,
    enableDebug,
  };
};
